"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import { useAnalytics } from "@/components/analytics/use-analytics";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary component that catches client-side errors
 * and logs them to Google Cloud Logging
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Use the enhanced logger to log the error with full context
    console.error(error, {
      source: "ErrorBoundary",
      componentName: this.props.componentName || "Unknown",
      componentStack: errorInfo.componentStack,
      route: typeof window !== "undefined" ? window.location.pathname : "",
      timestamp: new Date().toISOString(),
    });
    try {
      this.props.onError?.(error, errorInfo);
    } catch {
      // Never let analytics break the boundary.
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        this.props.fallback || (
          <div className="p-4 border border-red-300 rounded bg-red-50 text-red-900">
            <h2 className="text-lg font-medium">Something went wrong</h2>
            <p className="mt-1">Please try refreshing the page</p>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

/**
 * Client wrapper that injects the analytics capture callback into ErrorBoundary
 * without making the boundary depend on hooks directly.
 */
export function AnalyticsErrorBoundary({
  children,
  ...props
}: Omit<Props, "onError">) {
  const { captureException } = useAnalytics();
  return (
    <ErrorBoundary
      {...props}
      onError={(error, errorInfo) => {
        captureException(error, "ErrorBoundary", {
          component_name: props.componentName ?? "Unknown",
          component_stack: errorInfo.componentStack ?? null,
          route: typeof window !== "undefined" ? window.location.pathname : "",
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
